import React, { useState, useEffect } from 'react';
import { outlineApi } from '../../api/outlines';
import Button from '../Common/Button';
import Loading from '../Common/Loading';
import './Outline.css';

const MEDIUM_OPTIONS = [
  { value: 'novel', label: 'Novel Series' },
];

/**
 * TemplateSelector - Select an outline template for story or project.
 *
 * Props:
 * - medium: Initial medium (optional for project-level)
 * - isProjectLevel: Whether this is for a project-level outline
 * - onSelect: Callback with (templateKey, medium)
 * - onCancel: Cancel callback
 */
export function TemplateSelector({ medium: initialMedium, isProjectLevel = false, onSelect, onCancel }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [selectedMedium, setSelectedMedium] = useState(initialMedium || 'novel');

  const scope = isProjectLevel ? 'project' : 'story';
  const mediumForQuery = isProjectLevel ? selectedMedium : initialMedium;

  useEffect(() => {
    loadTemplates();
  }, [mediumForQuery, scope]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await outlineApi.getTemplates(mediumForQuery, scope);
      setTemplates(response.data || []);
    } catch (err) {
      setError('Failed to load templates');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    // Pass both template key and medium (for project-level)
    onSelect(selected, isProjectLevel ? selectedMedium : null);
  };

  if (loading) return <Loading />;
  if (error) return <div className="template-selector__error">{error}</div>;

  const title = isProjectLevel ? 'Choose a Series Outline Template' : 'Choose an Outline Template';
  const description = isProjectLevel
    ? 'Select a template for your multi-book series arc.'
    : 'Select a template to start with, or choose blank to build from scratch.';

  return (
    <div className="template-selector">
      <h3>{title}</h3>
      <p className="template-selector__description">{description}</p>

      {isProjectLevel && (
        <div className="template-selector__medium-picker">
          <label htmlFor="medium-select">Series Type:</label>
          <select
            id="medium-select"
            value={selectedMedium}
            onChange={(e) => {
              setSelectedMedium(e.target.value);
              setSelected(null); // Reset selection when medium changes
            }}
          >
            {MEDIUM_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      )}

      <div className="template-selector__list">
        <button
          className={`template-selector__item ${selected === null ? 'template-selector__item--selected' : ''}`}
          onClick={() => setSelected(null)}
        >
          <div className="template-selector__item-name">Blank Outline</div>
          <div className="template-selector__item-desc">Start with an empty outline</div>
          <div className="template-selector__item-count">0 sections</div>
        </button>

        {templates.map(template => (
          <button
            key={template.key}
            className={`template-selector__item ${selected === template.key ? 'template-selector__item--selected' : ''}`}
            onClick={() => setSelected(template.key)}
          >
            <div className="template-selector__item-name">{template.name}</div>
            <div className="template-selector__item-desc">{template.description}</div>
            <div className="template-selector__item-count">{template.section_count} sections</div>
          </button>
        ))}
      </div>

      <div className="template-selector__actions">
        <Button onClick={onCancel} variant="secondary">Cancel</Button>
        <Button onClick={handleConfirm} variant="primary">Create Outline</Button>
      </div>
    </div>
  );
}

export default TemplateSelector;
